import React, { useEffect, useRef } from 'react';

/**
 * Fireworks — the win-announcement overlay for game nights.
 *
 * A full-screen, pointer-transparent <canvas> that launches rockets from the
 * bottom of the screen and bursts them into multi-colour particle explosions.
 * Purely visual: no writes, no sound, nothing that can disturb play.
 *
 * Render it while a winner is on screen — `fireKey` (round|game|winner)
 * re-runs the effect for every NEW winner, so each announcement fires a
 * fresh volley. Rockets stop launching after `durationMs`; the sky then
 * empties itself and the parent unmounts the canvas.
 *
 * This is the PLAYER-APP copy of muuzbox-reports-admin/src/components/Games/
 * Fireworks.js (which is itself in step with the vanilla port in
 * muuzbox-games-display/renderer/fireworks.js). Keep all three visually in
 * step — a launcher guest and a PWA guest must see the same show.
 *
 * Brand hues: magenta + violet first (the house gradient), then gold, green,
 * cyan, orange — so every burst reads as Muuzbox.
 */
const HUES = [336, 270, 45, 150, 200, 15];

const Fireworks = ({ fireKey, durationMs = 7000, zIndex = 80 }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!fireKey) return undefined;
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let raf = 0;
    let running = true;

    const resize = () => {
      canvas.width = Math.floor(canvas.clientWidth * dpr);
      canvas.height = Math.floor(canvas.clientHeight * dpr);
    };
    resize();
    window.addEventListener('resize', resize);

    const rockets = [];
    const sparks = [];

    const launch = () => {
      rockets.push({
        x: canvas.width * (0.12 + Math.random() * 0.76),
        y: canvas.height + 10,
        vx: (Math.random() - 0.5) * 2.2 * dpr,
        vy: -(canvas.height * 0.013 + Math.random() * canvas.height * 0.004),
        targetY: canvas.height * (0.12 + Math.random() * 0.35),
        hue: HUES[(Math.random() * HUES.length) | 0],
      });
    };

    const explode = (r) => {
      const count = 70 + ((Math.random() * 50) | 0);
      const speed = canvas.height * 0.0075 * (0.8 + Math.random() * 0.5);
      for (let i = 0; i < count; i++) {
        const angle = (Math.PI * 2 * i) / count + Math.random() * 0.25;
        const v = speed * (0.5 + Math.random() * 0.65);
        sparks.push({
          x: r.x, y: r.y,
          vx: Math.cos(angle) * v,
          vy: Math.sin(angle) * v,
          life: 1,
          decay: 0.010 + Math.random() * 0.014,
          hue: r.hue + (Math.random() * 34 - 17),
          size: (1.3 + Math.random() * 1.9) * dpr,
        });
      }
    };

    const endAt = performance.now() + durationMs;
    let lastLaunch = 0;

    const frame = (t) => {
      if (!running) return;
      if (t < endAt && t - lastLaunch > 300) {
        lastLaunch = t;
        launch();
        if (Math.random() < 0.35) launch();   // occasional double-burst
      }
      // Fade the previous frame instead of clearing it — that IS the trail.
      ctx.globalCompositeOperation = 'destination-out';
      ctx.fillStyle = 'rgba(0,0,0,0.22)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.globalCompositeOperation = 'lighter';

      for (let i = rockets.length - 1; i >= 0; i--) {
        const r = rockets[i];
        r.x += r.vx;
        r.y += r.vy;
        r.vy += canvas.height * 0.00022;
        ctx.beginPath();
        ctx.fillStyle = `hsla(${r.hue},100%,72%,.95)`;
        ctx.arc(r.x, r.y, 2.2 * dpr, 0, Math.PI * 2);
        ctx.fill();
        if (r.y <= r.targetY || r.vy >= 0) { explode(r); rockets.splice(i, 1); }
      }
      for (let i = sparks.length - 1; i >= 0; i--) {
        const s = sparks[i];
        s.x += s.vx;
        s.y += s.vy;
        s.vx *= 0.985;
        s.vy = s.vy * 0.985 + canvas.height * 0.00016;
        s.life -= s.decay;
        if (s.life <= 0) { sparks.splice(i, 1); continue; }
        ctx.beginPath();
        ctx.fillStyle = `hsla(${s.hue},100%,${55 + s.life * 20}%,${s.life})`;
        ctx.arc(s.x, s.y, s.size * (0.55 + s.life * 0.75), 0, Math.PI * 2);
        ctx.fill();
      }
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, [fireKey, durationMs]);

  if (!fireKey) return null;
  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none',
        zIndex,
      }}
    />
  );
};

export default Fireworks;
